<script setup lang="ts">
import { watch, computed } from 'vue';
import { useForm } from 'vee-validate';
import { companyIncomeSchema } from '@/validation';
import type { CompanyIncome, CompanyIncomeEntry } from '@/types';
import type { BorrowerGroup } from '@/types/BorrowerGroup';
import type { ApplicantEntry } from '@/types/Applicant';
import { createCompanyIncome, getBorrowerDisplayName } from '@/helpers/builder';
import { BorrowerType, NET_PROFIT_DEFAULT_FREQUENCY, TrusteeType, netProfitPeriodOptions } from '@/helpers/const';

const props = withDefaults(
  defineProps<{
    modelValue: CompanyIncome | null;
    borrowerGroup: BorrowerGroup;
    applicants: ApplicantEntry[];
    showButtons?: boolean;
    disabled?: boolean;
  }>(),
  {
    showButtons: false,
    disabled: false,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', v: CompanyIncome): void;
  (e: 'save'): void;
  (e: 'cancel'): void;
}>();

const { meta, submitCount, handleSubmit, resetForm } = useForm<CompanyIncomeEntry>({
  validationSchema: companyIncomeSchema,
  validateOnMount: false,
});

const companyName = computed(() => {
  if (props.borrowerGroup.type === BorrowerType.COMPANY) {
    return props.borrowerGroup.companyDetails?.companyName ?? 'the company';
  }
  if (
    props.borrowerGroup.type === BorrowerType.TRUST &&
    props.borrowerGroup.trustDetails?.trusteeType === TrusteeType.COMPANY
  ) {
    return props.borrowerGroup.trustDetails?.companyDetails?.companyName ?? 'the company';
  }
  return getBorrowerDisplayName(props.borrowerGroup, props.applicants);
});

watch(
  () => props.modelValue,
  (data) => {
    const formValues: CompanyIncomeEntry = data ? { ...data } : createCompanyIncome(props.borrowerGroup.id);
    if (
      formValues.netProfit &&
      !netProfitPeriodOptions.includes(formValues.netProfit.frequency as (typeof netProfitPeriodOptions)[number])
    ) {
      formValues.netProfit = { ...formValues.netProfit, frequency: NET_PROFIT_DEFAULT_FREQUENCY };
    }
    resetForm({ values: formValues });
  },
  { immediate: true, deep: true },
);

const update = handleSubmit(
  (formValues) => {
    const companyIncome: CompanyIncome = {
      netProfit: formValues.netProfit ?? { amount: 0, frequency: NET_PROFIT_DEFAULT_FREQUENCY },
      borrowerGroupId: props.borrowerGroup.id,
    };
    emit('update:modelValue', companyIncome);
    return true;
  },
  () => false,
);

const save = async () => {
  if (await update()) {
    emit('save');
  }
};

const cancel = () => {
  resetForm();
  emit('cancel');
};

defineExpose({ update, cancel });
</script>

<template>
  <div>
    <h3 class="mb-4 text-lg font-medium">Company Income</h3>
    <p class="mb-6 text-gray-600">Please include {{ companyName }}'s net profit for the most recent financial year.</p>

    <BxField v-slot="{ field }" name="netProfit">
      <BxPeriodicAmount
        v-bind="field"
        :id="borrowerGroup.id + '-net-profit'"
        label="Net profit amount"
        label-type="floating"
        leading-symbol="$"
        class="form-control"
        :disabled="disabled"
        :period-options="[...netProfitPeriodOptions]"
      />
    </BxField>

    <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
      Please review the error messages above.
    </BxErrorPanel>

    <div v-if="props.showButtons && !disabled" class="mt-10 flex gap-4">
      <BxButton :id="`${borrowerGroup.id}-income-save-details`" variant="secondary" @click="save()">
        Save income details
      </BxButton>
      <BxButton variant="link" @click="cancel()">Cancel</BxButton>
    </div>
  </div>
</template>
